import type {CSSProperties} from 'react';
import {WEDGE_CALENDAR} from '@territory/tokens';
import {font, radius, weight} from '../scale.js';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeCalendarWelcomeLook = 'calendar-welcome-prep' | 'calendar-welcome-dismiss';

const LOOKS = new Set<string>(['calendar-welcome-prep', 'calendar-welcome-dismiss']);
export const isWedgeCalendarWelcomeLook = (look: string): look is WedgeCalendarWelcomeLook => LOOKS.has(look);

/* Released calendar welcome overlay (the card shown once when the Google calendar connects): two native controls.
   Prep my meetings is the one spend action. It is a full-width block 50px tall with a 14px radius, 15.5px bold type and a centered flex row with
   an 8px gap for its glyph and label. It takes the accent fill with the white ink of the calendar connect flow (the fixed role `WEDGE_CALENDAR.ink`).
   While the caller holds it with `disabled` (no pull is left to spend) it takes the elevated fill, the quaternary ink and the not-allowed cursor.
   Not now and Got it are one control: a bare full-width block with 14px of padding and 14px medium type in the secondary ink and the Wedge font,
   with no fill and no border. It sets no display, so the native button stays inline-block, unlike `calendar-later` of the preflight Sheet, which is a
   block. Each reads the exact legacy roles and keeps the declaration order of the source. The caller owns the glyph, the labels, the handlers and the
   4px top margin of the dismiss. The prep box is 50px tall, so a transparent 44px target could only repeat it: it draws none (`wedgeCalendarWelcomeDrawsHit` and a `content: none`
   rule in the hit sheet), as the field card save does. A browser measured the dismiss box at 48px tall at 390px and 1440px, so its region equals
   the box. The dismiss has the same values as `achievement-keep`, but it keeps the 44px target that `achievement-keep` drops, so it is its own look. */
const PREP_HEIGHT = 50, PREP_FONT = 15.5, PREP_GAP = 8;
const DISMISS_PADDING = 14;

export function wedgeCalendarWelcomeStyle(look: WedgeCalendarWelcomeLook, disabled = false): CSSProperties {
  const r = wedgeExactRole;
  if (look === 'calendar-welcome-prep') return {
    width: '100%', height: PREP_HEIGHT, borderRadius: radius.xl,
    background: disabled ? r('bgElev') : r('accent'), color: disabled ? r('labelQuad') : WEDGE_CALENDAR.ink,
    border: 0, cursor: disabled ? 'not-allowed' : 'pointer', fontSize: PREP_FONT, fontWeight: weight.bold, fontFamily: r('font'),
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: PREP_GAP,
  };
  return {
    width: '100%', background: 'none', border: 0, color: r('labelSec'),
    fontSize: font.body, fontWeight: weight.medium, padding: DISMISS_PADDING, cursor: 'pointer', fontFamily: r('font'),
  };
}

export const wedgeCalendarWelcomeDrawsHit = (look: string): boolean => look !== 'calendar-welcome-prep';
